/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import { MessageSquare, CheckCircle2, AlertTriangle, Save, Send, Trash2, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * SlackAlertsCard — Settings card for missed-call Slack webhook.
 *
 * Iter 134 (Apr 30, 2026). Admin pastes a Slack Incoming Webhook URL
 * once; thereafter any inbound CHUB call that rings-out unanswered fires
 * a rich Slack Block-Kit message into the configured channel with:
 *
 *   • Customer name (from CRM if matched) + phone
 *   • Department + last CRM agent who engaged them
 *   • "Open in CRM" deep-link button that pre-searches the phone
 *
 * The webhook URL is persisted in the backend (settings collection) so
 * it survives deploys; it's NEVER returned by any GET endpoint in
 * full-string form (only the last 6 chars + a configured boolean).
 *
 * Admins can also fire a test ping to verify the webhook works before
 * waiting for a real missed call.
 */

const SlackAlertsCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/slack-alerts/status");
      setStatus(data);
    } catch {
      setStatus({ configured: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const onSave = async () => {
    const url = webhookUrl.trim();
    if (!url) { toast.error("Paste a Slack webhook URL"); return; }
    if (!/^https:\/\/hooks\.slack\.com\//.test(url)) {
      toast.error("That doesn't look like a Slack webhook URL — expected https://hooks.slack.com/...");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/slack-alerts/set-webhook", { webhook_url: url });
      toast.success("Slack webhook saved ✓");
      setWebhookUrl("");
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    try {
      await api.post("/admin/slack-alerts/test-ping");
      toast.success("Test ping fired — check your Slack channel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Remove the Slack webhook? Missed-call alerts will stop.")) return;
    try {
      await api.delete("/admin/slack-alerts/webhook");
      toast.success("Slack webhook removed");
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div className="bento-card" data-testid="slack-alerts-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
            <MessageSquare size={18} className="text-indigo-600" />
          </div>
          <div>
            <h3 className="text-[13px] font-bold text-[--text-primary]">Missed-call Slack Alerts</h3>
            <p className="text-[11px] text-[--text-muted]">Ping your team the second an inbound call goes unanswered.</p>
          </div>
        </div>
        {loading ? null : status?.configured ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 size={12} /> Live
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-slate-50 text-slate-500 border border-slate-200">
            <AlertTriangle size={12} /> Off
          </span>
        )}
      </div>

      {/* Help block */}
      <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-600 leading-relaxed">
        <div className="flex items-start gap-2">
          <HelpCircle size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
          <div>
            1. In Slack, go to <strong>Apps → Incoming Webhooks → Add to Slack</strong>.<br />
            2. Pick the channel you want alerts in (e.g. <code>#sales-calls</code>).<br />
            3. Copy the <code>https://hooks.slack.com/services/...</code> URL and paste it below.<br />
            A missed inbound call fires one message with customer name, phone, department, and a deep-link back to the CRM chat.
          </div>
        </div>
      </div>

      {status?.configured && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-800">
          Webhook active · ending in <code className="font-mono font-bold">…{status.last6}</code>
          {status.last_alert_at && <span className="text-emerald-600"> · last alert {status.last_alert_at.slice(0, 10)}</span>}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Slack Webhook URL</label>
          <input
            type="url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder={status?.configured ? "Paste a new URL to replace the existing one" : "https://hooks.slack.com/services/T00000000/B00000000/XXXXXXX"}
            className="w-full px-3 py-2 text-[11px] border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-200 outline-none font-mono"
            data-testid="slack-webhook-input"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={onSave}
            disabled={saving || !webhookUrl.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="slack-save-btn"
          >
            <Save size={12} /> {saving ? "Saving…" : status?.configured ? "Update webhook" : "Save webhook"}
          </button>
          {status?.configured && (
            <>
              <button
                onClick={onTest}
                disabled={testing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="slack-test-btn"
              >
                <Send size={12} /> {testing ? "Sending…" : "Send test ping"}
              </button>
              <button
                onClick={onDelete}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                data-testid="slack-delete-btn"
              >
                <Trash2 size={12} /> Remove
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SlackAlertsCard;
